import { PHONE_E164_PATTERN, PHONE_MAX_CHARS, normalizePhone } from "@sagot/constants";
import { z } from "zod";
import { CONSENT_KEY, CONTACT_RULE_EMAIL_OR_PHONE, EMAIL_FIELD, FIELD_MAX_CHARS, MOBILE_FIELD, TEXTAREA_MAX_CHARS } from "./constants";
import { MULTI_CHOICE_SEPARATOR, type ActionField } from "./contract";
import { DEFAULT_ACTION_LABELS, type ActionLabels } from "./labels";

/** Phone is optional contact data, international E.164 (DEC-041). */
export const normalizeMobile = normalizePhone;

export type FormValues = Record<string, string>;

export const isMobileField = (field: ActionField): boolean => field.type === "tel" || field.name === MOBILE_FIELD;

export function maxCharsOf(field: ActionField): number {
  if (isMobileField(field)) return PHONE_MAX_CHARS;
  return field.max_length ?? (field.type === "textarea" ? TEXTAREA_MAX_CHARS : FIELD_MAX_CHARS);
}

/** Ticked choices (react-hook-form gives `false` for none, a string for one, an array for more) → one string. */
function choicesSchema(field: ActionField, labels: ActionLabels): z.ZodTypeAny {
  const options = new Set(field.options ?? []);
  const picked = z
    .union([z.array(z.string()), z.string(), z.boolean()])
    .transform((value) => (Array.isArray(value) ? value : typeof value === "string" && value ? [value] : []).filter((choice) => options.has(choice)));
  const checked = field.required ? picked.refine((choices) => choices.length > 0, labels.required) : picked;
  return checked.transform((choices) => choices.join(MULTI_CHOICE_SEPARATOR));
}

function fieldSchema(field: ActionField, labels: ActionLabels): z.ZodTypeAny {
  if (field.type === "checkboxes") return choicesSchema(field, labels);
  const mobile = isMobileField(field);
  let schema: z.ZodTypeAny = z
    .string()
    .trim()
    .max(maxCharsOf(field))
    .transform((value) => (mobile ? normalizeMobile(value) : value));
  // The phone is always optional on the client, even when the server marks it required.
  if (field.required && !mobile) schema = schema.refine((value: string) => value.length > 0, labels.required);
  if (mobile) schema = schema.refine((value: string) => value.length === 0 || PHONE_E164_PATTERN.test(value), labels.invalidMobile);
  if (field.type === "email") {
    schema = schema.refine((value: string) => value.length === 0 || z.string().email().safeParse(value).success, labels.invalidEmail);
  }
  return schema;
}

/**
 * zod schema from the server-provided field list: per-field rules, the `email_or_phone` contact
 * rule (message on both fields) and the required privacy-notice checkbox. All cross-field checks
 * live in one refinement so every error shows at once.
 */
export function buildActionSchema(fields: readonly ActionField[], labels: ActionLabels = DEFAULT_ACTION_LABELS, contactRule?: string) {
  const shape: Record<string, z.ZodTypeAny> = { [CONSENT_KEY]: z.boolean() };
  for (const field of fields) shape[field.name] = fieldSchema(field, labels);
  const names = new Set(fields.map((field) => field.name));
  const needsContact = contactRule === CONTACT_RULE_EMAIL_OR_PHONE;
  return z.object(shape).superRefine((values, ctx) => {
    if (values[CONSENT_KEY] !== true) ctx.addIssue({ code: z.ZodIssueCode.custom, path: [CONSENT_KEY], message: labels.consentRequired });
    if (!needsContact || values[EMAIL_FIELD] || values[MOBILE_FIELD]) return;
    for (const name of [EMAIL_FIELD, MOBILE_FIELD]) {
      if (names.has(name)) ctx.addIssue({ code: z.ZodIssueCode.custom, path: [name], message: labels.contactRule });
    }
  });
}

/** Initial form values: server pre-fill (what the visitor already said); selects start unselected. */
export function initialValues(fields: readonly ActionField[]): Record<string, unknown> {
  const values: Record<string, unknown> = { [CONSENT_KEY]: false };
  for (const field of fields) values[field.name] = field.type === "checkboxes" ? [] : (field.value ?? "");
  return values;
}

/** Message of a react-hook-form error entry (a field error or a nested/merged one). */
export function errorMessage(error: unknown): string | undefined {
  if (typeof error === "object" && error !== null && "message" in error && typeof error.message === "string") return error.message;
  return undefined;
}

/** Field values as the confirm request carries them: non-empty strings only. */
export function toArguments(values: FormValues): Record<string, string> {
  return Object.fromEntries(Object.entries(values).filter(([, value]) => value !== ""));
}
